import { useEffect, useState } from "react";
import { useLazyGetAcademyHomeDataQuery } from "../../store/api/academyApi";
import { setDropdownList } from "../../store/slices/academySlice";
import { useDispatch } from "react-redux";
import BecomeMember from "../common/membership";
import { openModal } from "../../store/slices/modalSlice";
import { useAppSelector } from "../../hooks/store-hooks";
import { MODAL_NAME } from "../../constants";
import LockedOverlay from "../common/locked_content";
import { formatLearnersCount } from "../../helpers";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants/routes";
import { formatDuration } from "../../helpers";
import { trackEvent } from "../../analytics";
export const DropdownList: any = [
  { title: "All", id: "all" },
  { title: "Courses", id: "courses" },
  { title: "Livestream", id: "livestream" },
  { title: "News", id: "news" },
];

const AcademyCourseButtonMap: { [key: string]: string } = {
  START: "Start Learning!",
  RESUME: "Resume",
  COMPLETED: "Completed",
  UNLOCK: "Unlock",
};

const AcademyHomeComponent = () => {
  const { modalName } = useAppSelector((state) => state.modal);
  const [triggerGetAcademyAllTabData] = useLazyGetAcademyHomeDataQuery();
  const [academyData, setAcademyData] = useState<any>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchAcademyData = async () => {
    const resp = await triggerGetAcademyAllTabData({
      type: "all",
    }).unwrap();
    setAcademyData(resp.data);
  };

  useEffect(() => {
    fetchAcademyData();
    dispatch(setDropdownList(DropdownList));
  }, []);

  const gridOverlayStyleNews = {
    background: "linear-gradient(180deg, rgba(0, 0, 0, 0) 48%, #000000 100%)",
  };

  const navigateToNews = (index: number) => {
    navigate(ROUTES.ACADEMY_NEWS, {
      state: {
        isModalOpen: true,
        selectedNews: index,
        modalType: "bytes",
      },
    });
  };
  const renderCourses = (course: any) => {
    return (
      <div className="w-full relative bg-primary-250 text-white rounded-lg overflow-hidden shadow-lg">
        {/* Aspect ratio container */}
        <div className="relative pb-[56.25%]">
          <img
            src={course?.thumbnail || "/placeholder.svg"}
            alt="Thumbnail"
            className="absolute inset-0 w-full h-full object-cover rounded-lg opacity-100"
          />
          <div className="absolute inset-0 z-10 p-4 flex flex-col justify-between">
            <div>
              <p className="2xl:text-[28px] text-[17px]">
                {course?.lessons_count} Lessons •{" "}
                {formatDuration(course?.content_duration)}
              </p>
              <h3 className="font-bold 2xl:text-[38px] xl:text-[28px] text-[20px] w-3/5 py-2 2xl:leading-[38px] xl:leading-[32px] leading-[20px]">
                {course?.title}
              </h3>
            </div>

            <div>
              <button
                className="bg-blue-500 text-white xl:py-2 py-1 px-4 rounded-lg w-fit"
                type="button"
                onClick={() => {
                  if (!course.is_subscribed) {
                    dispatch(openModal({ modalName: "BECOME_MEMBER" }));
                  } else {
                    trackEvent("course_module_list_opened", {
                      course_id: course?.course_id,
                      course_title: course?.title,
                    });
                    navigate(`/academy/courses/${course.course_id}`);
                  }
                }}
              >
                {AcademyCourseButtonMap[course?.footer?.course_status]}
              </button>

              {course?.footer?.course_status === "RESUME" ? (
                <div className="flex flex-col justify-between mt-4 w-2/5 gap-2">
                  <div className="w-full bg-gray-700 rounded-lg h-3">
                    <div
                      className="bg-gradient-text h-3 rounded-lg"
                      style={{
                        width:
                          `${course?.footer?.progress?.completion_percentage}%` ||
                          0,
                      }}
                    ></div>
                  </div>
                  <p className="text-xs font-semibold">
                    {course?.footer?.progress?.completion_percentage || 0}%
                    Complete
                  </p>
                </div>
              ) : (
                <div className="flex items-center pt-2">
                  <div
                    className={`flex -space-x-4 rtl:space-x-reverse float-left ${
                      course?.footer?.learners?.count === 0 && "hidden"
                    }`}
                  >
                    {/* Profile images */}
                    {course?.footer?.learners?.profile_pics?.map(
                      (profilePic: string, index: number) => (
                        <div
                          key={index}
                          className="w-8 h-8 border-2 border-[#EBEBEB] rounded-full overflow-hidden relative"
                        >
                          <img
                            className="w-full h-full absolute inset-0 object-cover"
                            src={profilePic || "/placeholder.svg"}
                            alt="Profile"
                          />
                        </div>
                      ),
                    )}
                  </div>
                  <p className="ml-2 text-sm text-white font-semibold">
                    {formatLearnersCount(course?.footer?.learners?.count || 0)}+
                    Learners
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLivestreams = (livestream: any) => {
    const handleLivestreamClick = () => {
      if (!livestream.is_subscribed) {
        dispatch(openModal({ modalName: "BECOME_MEMBER" }));
      } else {
        navigate(`${ROUTES.ACADEMY_LIVESTREAM}/${livestream.livestream_id}`);
      }
    };

    return (
      <a
        className="relative text-white rounded-lg shadow-lg aspect-video block overflow-hidden"
        onClick={handleLivestreamClick}
      >
        <img
          src={livestream?.thumbnail}
          alt="Thumbnail"
          className="absolute inset-0 w-full h-full object-cover rounded-lg z-10"
        />
        {livestream?.is_subscribed ? null : <LockedOverlay blur={false} />}
      </a>
    );
  };

  const renderNews = (news_list: any) => {
    return (
      <div className="flex flex-row gap-4 rounded-lg overflow-x-scroll">
        {news_list &&
          news_list.map((news: any, index: number) => (
            <div
              key={index}
              className="relative bg-primary-250 h-52 w-full max-w-[150px] max-h-[200px] flex-shrink-0"
              onClick={() => navigateToNews(index)}
            >
              <img
                src={news?.thumbnail}
                alt="Placeholder"
                className="absolute w-full h-full object-cover opacity-100"
              />
              <div className="absolute inset-0" style={gridOverlayStyleNews} />
              <div className="relative z-10 p-2 flex flex-col justify-end h-full w-full rounded-lg">
                <p className="absolute bottom-5 left-5 font-bold text-[15px] text-white ">
                  {news.heading && news.heading.length > 20
                    ? `${news.heading.slice(0, 20)}...`
                    : news.heading || "30-SEC BYTES"}
                </p>
              </div>
            </div>
          ))}
      </div>
    );
  };

  return (
    <div className="w-full lg:w-full flex flex-col gap-4">
      {/* Courses */}
      <section className="bg-white p-4 md:p-6 rounded-lg shadow-lg mb-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-700">Courses</h2>
          <a href={ROUTES.ACADEMY_COURSES} className="text-blue-500">
            View all
          </a>
        </div>
        <div className="w-full flex flex-row gap-6">
          {academyData?.courses?.courses_list
            ?.slice(0, 2)
            .map((course: any) => (
              <div key={course.course_id} className="w-1/2">
                {renderCourses(course)}
              </div>
            ))}
        </div>
      </section>

      {/* Past Livestreams */}
      <section className="bg-white p-4 md:p-6 rounded-lg shadow-lg mb-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-700">Past Livestreams</h2>
          <a href={ROUTES.ACADEMY_LIVESTREAM} className="text-blue-500">
            View all
          </a>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 ">
          {academyData?.livestreams?.livestreams_list?.map((livestream: any) =>
            renderLivestreams(livestream),
          )}
        </div>
      </section>

      {/* News and Comics in a Row */}
      <section className="grid grid-cols-1 ">
        {/* News */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-700 ">News</h2>
            <a href={ROUTES.ACADEMY_NEWS} className="text-blue-500">
              View all
            </a>
          </div>
          {academyData?.news?.news_list
            ? renderNews(academyData.news.news_list)
            : null}
        </div>

        {/* Comics */}
        {/* <div className="bg-white p-4 md:p-6 rounded-lg shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-700">Comics</h2>
            <a href={ROUTES.ACADEMY_COMICS} className="text-blue-500">
              View all
            </a>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {academyData?.comics?.comics_list?.map((comic: any) =>
              renderComics(comic),
            )}
          </div>
        </div> */}
      </section>
      {modalName === MODAL_NAME.BECOME_MEMBER && <BecomeMember />}
    </div>
  );
};

export default AcademyHomeComponent;
